import { axiosPost } from '@gr/portal/authAxios';
import {
  Auth0GridUser,
  IHttpResponse,
  ISearchResponse
} from '@gr/shared/models';
import { useCallback, useState } from 'react';

interface UseUsersResult {
  data: ISearchResponse<Auth0GridUser> | null;
  loading: boolean;
  error: any;
}

const useUsers = (): [UseUsersResult, () => Promise<void>] => {
  const [data, setData] = useState<ISearchResponse<Auth0GridUser> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const fetchAllUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let allUsers: Auth0GridUser[] = [];
      let currentPage = 0;
      let totalCount = 0;
      let hasMore = true;
      const MAX_PAGES = 100; // Safety limit to prevent infinite loops

      // Fetch users page by page
      while (hasMore) {
        const response = await axiosPost<IHttpResponse<ISearchResponse<Auth0GridUser>>>(
          '/auth0-get-users',
          { page: currentPage }
        );

        const pageData = response.data.data;
        totalCount = pageData.totalCount;
        const recordsCount = pageData.records.length;
        allUsers = [...allUsers, ...pageData.records];

        console.log(`Fetched page ${currentPage}: ${recordsCount} users, Total: ${allUsers.length}/${totalCount}`);

        // Safety checks to prevent infinite loops
        if (allUsers.length >= totalCount) {
          // Successfully fetched all users
          hasMore = false;
        } else if (recordsCount === 0) {
          // Empty page returned - stop to prevent infinite loop
          console.warn(`Empty page returned at page ${currentPage}. Stopping pagination.`);
          hasMore = false;
        } else if (currentPage >= MAX_PAGES) {
          // Exceeded maximum pages - stop to prevent infinite loop
          console.error(`Exceeded maximum page limit (${MAX_PAGES}). Stopping pagination.`);
          hasMore = false;
        } else {
          currentPage++;
        }
      }

      setData({
        totalCount,
        records: allUsers
      });
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  return [{ data, loading, error }, fetchAllUsers];
};

export default useUsers;
